import React, { useState, useContext, useEffect, useRef } from "react";
import { gql, useQuery, useMutation, useApolloClient } from "@apollo/client";
import styled from "styled-components";
import { FaPlus } from "react-icons/fa";
import { TbRefresh } from "react-icons/tb";
import {
  LoadingView,
  NotificationContext,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import StatusDropDown from "../../dropdowns/StatusDropDown";
import { useAuth } from "../../contexts/AuthContext";
import { useUserList } from "../../contexts/UserListContext";
import { Button } from "@solsys/solsys-reactjs/dist/BasicLayout";

import LoadImage from "../elements/loadImage";
import ChatSendButton from "../elements/ChatSendButton";
import { useIdempotentChatSubmit, mutationInput, createIdempotencyKey } from "../../utils/idempotency";
import { PROSPECT_EVENT_TYPE, isImageTypeId } from "../../eventTypes";
// Styled Components
const EventContainer = styled.div`
  background: #f0f0f0;
  padding: 20px;
  border-radius: 8px;
  max-width: 100%;
  //overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  font-family: "Arial", sans-serif;
  margin: 10px auto;
`;

const MessageBubble = styled.div`
  background-color: #fff;
  color: #333;
  padding: 12px 18px;
  border-radius: 20px;
  margin: 8px 0;
  max-width: 70%;
  align-self: ${({ own }) => (own ? "flex-end" : "flex-start")};
  color: ${({ own }) => (own ? "white" : "initial")};
  background-color: ${({ own }) => (own ? "#538cff" : "initial")};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  margin-right: ${({ own }) => (own ? "20px" : "0")};
  margin-left: ${({ own }) => (own ? "0" : "20px")};

  .changes {
    background-color: #ddf;
    color: #111;
    margin-top: 6px;
    padding: 6px;
    border-radius: 8px;
    font-style: italic;
    color: ${({ own }) => (own ? "white" : "initial")};
  }

  .time {
    font-size: 11px;
    font-style: italic;
    margin-top: 4px;
    color: #666;
    color: ${({ own }) => (own ? "white" : "initial")};
  }
`;

const MessageInput = styled.div`
  //display: flex;
  //align-items: center;
  background-color: #e8e8e8;
  padding: 10px;
  border-radius: 20px;
  margin-top: 10px;
`;

const Input = styled.input`
  border: none;
  outline: none;
  flex-grow: 1;
  padding: 8px 10px;
  border-radius: 20px;
  background: #fff;
`;

const ControlPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 5px 0;
`;

const ControlLabel = styled.div`
  font-size: 14px;
  color: #333;
  margin-right: 10px;
`;

const entityQuery = gql`
  query prospectEventQuery(
    $prospect_event_list_request: prospect_event_list_request
    $prospect_list_request: prospect_list_request
  ) {
    prospect_event(prospect_event_list_request: $prospect_event_list_request) {
      edges {
        id
        created_at
        type_id
        prospect_event_type {
          id
          name
        }
        message
        changes
        user_display_name
        user_id
        prospect {
          status_id
        }
      }
    }
    prospect(prospect_list_request: $prospect_list_request) {
      edges {
        status_id
        status {
          name
        }
      }
    }
  }
`;

const entityMutation = gql`
  mutation prospectEventMutate(
    $prospect_event_mutation_request: MutationInput
  ) {
    prospect_event(
      prospect_event_mutation_request: $prospect_event_mutation_request
    ) {
      id
      isNew
    }
  }
`;

const prospectMutation = gql`
  mutation prospectMutate($prospect_mutation_request: MutationInput) {
    prospect(prospect_mutation_request: $prospect_mutation_request) {
      id
      isNew
    }
  }
`;

const prospectEventCheckQuery = gql`
  query prospectEventCheckQuery(
    $prospect_event_check_list_request: prospect_event_check_list_request
  ) {
    prospect_event_check(prospect_event_check_list_request: $prospect_event_check_list_request) {
      edges {
        id
        receiver_id
        prospect_id
      }
    }
  }
`;

const subscriptionQuery = gql`
  query prospectEventQuery(
    $prospect_subscription_list_request: prospect_subscription_list_request
    $prospect_notification_list_request: prospect_notification_list_request
  ) {
    prospect_subscription(prospect_subscription_list_request: $prospect_subscription_list_request) {
      edges {
        id
        prospect_id
        user_id
        created_at
      }
    }
    prospect_notification(prospect_notification_list_request: $prospect_notification_list_request) {
      edges {
        id
        prospect_id
        user_id
        sender_id
      }
    }
  }
`;

const prospectSubscription = gql`
  mutation prospectSubscriptionMutate($prospect_subscription_mutation_request: MutationInput) {
    prospect_subscription(prospect_subscription_mutation_request: $prospect_subscription_mutation_request) {
      id
      isNew
    }
  }
`;

const eventCheckMutation = gql`
  mutation prospectEventCheckMutate($prospect_event_check_mutation_request: MutationInput) {
    prospect_event_check(prospect_event_check_mutation_request: $prospect_event_check_mutation_request) {
      id
      isNew
    }
  }
`;

const serverUrl = process.env.REACT_APP_API_URL || "RUNTIME_REPLACE_REACT_APP_API_URL";

// Prospect Event Component
const ProspectEvent = ({ prospect_id }) => {
  const { addNotification } = useContext(NotificationContext);
  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [status, setStatus] = useState();
  const [statusName, setStatusName] = useState();
  const [subscriptionId, setSubscriptionId] = useState(0);
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const client = useApolloClient();
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const selfSubKeyRef = useRef(createIdempotencyKey());
  const attachmentSig = (images || [])
    .map((file) => `${file.name}:${file.size}:${file.lastModified}`)
    .join(",");
  const {
    textKey,
    ensureUploaded,
    trackSend,
    isSending,
    resetAfterSuccess,
  } = useIdempotentChatSubmit({
    intentFingerprint: `${newMessage}\0${status ?? ""}\0${attachmentSig}`,
    images,
    serverUrl,
    onUploadError: () => addNotification("bad", "Upload Failed"),
  });

  const userList = useUserList().filter(item => item.id != user._id).map(item => item.username);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // variables for entity query const
  let variables = {
    prospect_event_list_request: {
      filters: [
        {
          field: "prospect_id",
          operator: "=",
          value: String(prospect_id),
        },
      ],
      sorting: {
        field: "id",
        direction: "desc",
      },
    },
    prospect_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(prospect_id),
        },
      ],
    },
  };

  // fetches entity and sets up refetch
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error(error);
    },
  });
  const events = data?.prospect_event?.edges ?? [];
  const prospect = data?.prospect?.edges?.[0];

  // sets up mutation
  const [mutateFunction, mutateInfo] = useMutation(entityMutation);
  const [mutateProspectFunction, mutateProspectInfo] =
    useMutation(prospectMutation);
  const getSubscriptionId = async () => {
    try {
      const response = await client.query({
        query: subscriptionQuery,
        variables: {
          prospect_subscription_list_request: {
            filters: [
              {
                field: "prospect_id",
                operator: "=",
                value: String(prospect_id),
              },
              {
                field: "user_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
          prospect_notification_list_request: {
            filters: [
              {
                field: "prospect_id",
                operator: "=",
                value: String(prospect_id),
              },
              {
                field: "sender_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
        },
        fetchPolicy: "network-only"
      });
      const prospect_subscriptions = response?.data?.prospect_subscription?.edges ?? [];
      if (prospect_subscriptions.length > 0) {
        setSubscriptionId(prospect_subscriptions[0].id);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  const updateReadStatus = async (data) => {
    try {
      const response = await client.mutate({
        mutation: eventCheckMutation,
        variables: {
          prospect_event_check_mutation_request: {
            id: data.id,
            properties: [
              { key: "prospect_id", number: prospect_id },
              { key: "receiver_id", string: user._id },
              { key: "read_status", number: 1 },
            ],
          },
        },
      });
    } catch (err) {
      console.error(err);
    }
  }

  const getEventCheck = async () => {
    try {
      const response = await client.query({
        query: prospectEventCheckQuery,
        variables: {
          prospect_event_check_list_request: {
            filters: [
              {
                field: "prospect_id",
                operator: "=",
                value: String(prospect_id),
              },
              {
                field: "receiver_id",
                operator: "=",
                value: String(user._id),
              },
              {
                field: "read_status",
                operator: "=",
                value: String(0),
              },
            ]
          },
        },
        fetchPolicy: "network-only"
      });
      const result = response?.data?.prospect_event_check?.edges ?? [];
      if (result.length > 0) {
        updateReadStatus(result[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  const sendMessage = () =>
    trackSend(async () => {
      const messageKey = textKey();
      const sentMessage = newMessage;
      const sentStatus = status;
      const sentStatusName = statusName;
      let promises = Promise.resolve();
      let changes = "";
      const url = new URL(window.location.href);
      const params = url.searchParams;
      if (params.has("tab")) {
        params.set("tab", "Chat");
      } else {
        params.append("tab", "Chat");
      }
      const pageUrl = url.toString();

      if (previewImages && previewImages.length > 0) {
        setIsUploading(true);
        let uploaded = [];
        try {
          uploaded = await ensureUploaded();
        } finally {
          setIsUploading(false);
        }
        if (!uploaded.length) return;

        for (const { key, path } of uploaded) {
          promises = promises.then(() =>
            mutateFunction({
              variables: {
                prospect_event_mutation_request: {
                  idempotency_key: key,
                  properties: [
                    { key: "type_id", number: PROSPECT_EVENT_TYPE.IMAGE },
                    { key: "message", string: path },
                    { key: "prospect_id", number: prospect_id },
                    { key: "user_id", string: user._id },
                    {
                      key: "user_display_name",
                      string: user.name ?? user.username,
                    },
                    { key: "url", string: pageUrl },
                  ],
                },
              },
            })
          );
        }
      }
      if (!!status) {
        changes += `Status changed from ${prospect?.status?.name} to ${statusName}`;
        promises = promises.then(() =>
          mutateProspectFunction({
            variables: {
              prospect_mutation_request: {
                id: prospect_id,
                properties: [{ key: "status_id", number: status }],
              },
            },
          })
        );
      }
      if (newMessage.trim() !== "" || !!status) {
        promises = promises.then(() =>
          mutateFunction({
            variables: {
                prospect_event_mutation_request: {
                  idempotency_key: messageKey,
                properties: [
                  { key: "type_id", number: PROSPECT_EVENT_TYPE.MESSAGE },
                  { key: "message", string: newMessage },
                  ...(changes === ""
                    ? []
                    : [{ key: "changes", string: changes }]),
                  { key: "prospect_id", number: prospect_id },
                  { key: "user_id", string: user._id },
                  {
                    key: "user_display_name",
                    string: user.name ?? user.username,
                  },
                  { key: "url", string: pageUrl },
                ],
              },
            },
          })
        );
      }

      return promises
        .then(() => {
          refetchEntity();
          setNewMessage((current) => (current === sentMessage ? "" : current));
          setStatus((current) => (current === sentStatus ? null : current));
          setStatusName((current) => (current === sentStatusName ? null : current));
          setImages([]);
          setPreviewImages([]);
          if (fileInputRef.current) fileInputRef.current.value = "";
          resetAfterSuccess();
        })
        .catch((error) => {
          addNotification("bad", "Something went wrong.");
          console.error(error);
        });
    });

  const subscribeProspect = async () => {
    try {
      let id = 0;
      const response = await client.query({
        query: subscriptionQuery,
        variables: {
          prospect_subscription_list_request: {
            filters: [
              {
                field: "prospect_id",
                operator: "=",
                value: String(prospect_id),
              },
              {
                field: "user_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
          prospect_notification_list_request: {
            filters: [
              {
                field: "prospect_id",
                operator: "=",
                value: String(prospect_id),
              },
              {
                field: "sender_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
        },
        fetchPolicy: "network-only"
      });
      const prospect_subscriptions = response?.data?.prospect_subscription?.edges ?? [];
      if (prospect_subscriptions.length > 0) {
        id = prospect_subscriptions[0].id;
      }
      if (subscriptionId != id) {
        if (subscriptionId) {
          addNotification("good", "Unassigned successfully!");
        } else {
          addNotification("good", "Assigned successfully!");
        }
        setSubscriptionId(id);
        return;
      }
      const subscriptions = await client.mutate({
        mutation: prospectSubscription,
        variables: {
          prospect_subscription_mutation_request: mutationInput({
            id: subscriptionId > 0 ? subscriptionId : undefined,
            shouldDelete: subscriptionId > 0,
            properties: [
              { key: "prospect_id", number: prospect_id },
              { key: "user_id", string: user._id },
            ],
            idempotencyKey:
              subscriptionId > 0 ? undefined : selfSubKeyRef.current,
          }),
        },
      });
      if (subscriptionId) {
        setSubscriptionId(0);
        addNotification("good", "Unassigned successfully!");
      } else {
        setSubscriptionId(subscriptions?.data?.prospect_subscription?.id);
        selfSubKeyRef.current = createIdempotencyKey();
        addNotification("good", "Assigned successfully!");
      }
    } catch (err) {
      addNotification("bad", "Something went wrong.");
      console.error(err);
    }
  }

  const refreshProspectEvent = () => {
    refetchEntity();
  }

  const handleChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Detect "@" and show suggestions
    const match = value.match(/@(\w*)$/);
    if (match) {
      const search = match[1].toLowerCase();
      const filteredUsers = userList.filter((user) =>
        user.toLowerCase().startsWith(search)
      );
      setSuggestions(filteredUsers);
      setShowSuggestions(filteredUsers.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectUser = (user) => {
    setNewMessage((prev) => prev.replace(/@\w*$/, `@${user} `)); // Replace typed text with selected user
    setShowSuggestions(false);
    inputRef.current.focus();
  };
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setImages(files);
    const previews = files.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews)
      .then(results => setPreviewImages(results))
      .catch(err => console.error('Image preview error', err));
  };

  const removeImage = (index) => {
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    localStorage.setItem('channel', `prospect-${prospect_id}`);
    const broadcast = new BroadcastChannel("notification-channel");
    broadcast.onmessage = (event) => {
      if (event.data && event.data.data && event.data.data.url) {
        const url = new URL(event.data.data.url);
        const path = url.pathname.startsWith("/") ? url.pathname.substring(1) : url.pathname;
        const pathSegments = path.split("/");

        let channel = "";

        if (pathSegments.length >= 3) {
          if (pathSegments[1] === "edit") {
            if (pathSegments[0] === "ecrane") {
              channel = `ecrane-${pathSegments[2]}`;
            } else if (pathSegments[0] === "prospect") {
              channel = `prospect-${pathSegments[2]}`;
            }
          }
        }
        if (channel == localStorage.getItem('channel')) {
          refetchEntity();
          getEventCheck();
        }
      }
    };
    getSubscriptionId();
    getEventCheck();
    return () => {
      localStorage.removeItem('channel');
      broadcast.close(); // Clean up when component unmounts
    }
  }, []);

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <EventContainer>
      <MessageInput>
        <div
          style={ {
            display: isMobile ? "block" : "flex",
            alignItems: "center",
            marginBottom: "8px",
            justifyContent: "space-between",
          } }
        >
          { isMobile ? (
            <div style={ { display: "block" } }>
              <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" } }>
                <div style={ { display: "flex" } }>
                  <h4 style={ { margin: 0, marginRight: "5px" } }>Update prospect</h4>
                  <TbRefresh size={ 19 } title="Refresh Chat" style={ { cursor: "pointer" } } onClick={ refreshProspectEvent } />
                </div>
                <Button onClick={ subscribeProspect } style={ { marginLeft: "10px" } }>{ subscriptionId ? "Unassign" : "Assign" }</Button>
              </div>
              <div
                style={ {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isMobile ? "space-between" : "end",
                } }
              >
                <ControlLabel>Change Status:</ControlLabel>
                <StatusDropDown
                  selected={ status }
                  onChange={ (id, status) => {
                    setStatus(id);
                    if (id) {
                      setStatusName(status.name);
                    } else {
                      setStatusName(null);
                    }
                  } }
                  filters={ [
                    {
                      field: "id",
                      operator: "!=",
                      value: String(prospect?.status_id),
                    },
                  ] }
                />
              </div>
            </div>
          ) : (
            <>
              <div style={ { display: "flex" } }>
                <h4 style={ { margin: 0, marginRight: "5px" } }>Update Prospect</h4>
                <TbRefresh size={ 19 } title="Refresh Chat" style={ { cursor: "pointer" } } onClick={ refreshProspectEvent } />
              </div>
              <div
                style={ {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                } }
              >
                <ControlLabel>Change Status:</ControlLabel>
                <StatusDropDown
                  selected={ status }
                  onChange={ (id, status) => {
                    setStatus(id);
                    if (id) {
                      setStatusName(status.name);
                    } else {
                      setStatusName(null);
                    }
                  } }
                  filters={ [
                    {
                      field: "id",
                      operator: "!=",
                      value: String(prospect?.status_id),
                    },
                  ] }
                />
                <Button onClick={ subscribeProspect } style={ { marginLeft: "10px" } }>{ subscriptionId ? "Unassign" : "Assign" }</Button>
              </div>
            </>
          ) }
        </div>
        <div style={ { display: "flex", alignItems: "center", position: "relative" } }>
          <Input
            type="text"
            placeholder="Type a message..."
            value={ newMessage }
            onChange={ handleChange }
            onKeyPress={ (e) => e.key === "Enter" && sendMessage() }
            ref={ inputRef }
          />
          { showSuggestions && (
            <ul
              style={ {
                position: "absolute",
                background: "white",
                border: "1px solid #ccc",
                listStyle: "none",
                padding: "5px",
                margin: 0,
                top: "100%",
                left: 0,
                width: "200px",
              } }
            >
              { suggestions.map((user) => (
                <li
                  key={ user }
                  style={ { padding: "5px", cursor: "pointer" } }
                  onClick={ () => handleSelectUser(user) }
                >
                  @{ user }
                </li>
              )) }
            </ul>
          ) }
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={ handleImageChange }
            disabled={ isUploading }
            ref={ fileInputRef }
            style={ { display: 'none' } }
          />
          <FaPlus style={ { marginLeft: '10px' } } size={ 20 } onClick={ triggerFileInput } disabled={ isUploading } />
          <ChatSendButton
            onClick={ sendMessage }
            sending={ isSending }
            disabled={
              newMessage.trim() === "" &&
              previewImages.length === 0 &&
              (status === prospect?.status_id || !status)
            }
          />
        </div>
        <div>
          { previewImages.length > 0 && (
            <div style={ { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' } }>
              { previewImages.map((img, idx) => (
                <div
                  key={ idx }
                  style={ {
                    position: 'relative',
                    width: '100px',
                    height: '100px'
                  } }
                >
                  <img
                    src={ img }
                    alt={ `preview-${idx}` }
                    style={ {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '6px'
                    } }
                  />
                  <div
                    onClick={ () => removeImage(idx) }
                    style={ {
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      cursor: 'pointer'
                    } }
                  >
                    ×
                  </div>
                </div>
              )) }
            </div>
          ) }
        </div>
      </MessageInput>
      <ControlPanel>
        {/*<div style={{ display: 'flex', alignItems: 'center' }}>*/ }
        {/*  <ControlLabel>Change Status:</ControlLabel>*/ }
        {/*  <StatusDropDown*/ }
        {/*    selected={status}*/ }
        {/*    onChange={setStatus}*/ }
        {/*    filters={[{*/ }
        {/*      field: 'id',*/ }
        {/*      operator: '!=',*/ }
        {/*      value: String(project?.status_id)*/ }
        {/*    }]}*/ }
        {/*  />*/ }
        {/*</div>*/ }
        {/*<IconButton title="Transfer"><FaExchangeAlt /><ButtonLabel>Transfer</ButtonLabel></IconButton>*/ }
        {/*<IconButton title="Share"><FaShare /><ButtonLabel>Share</ButtonLabel></IconButton>*/ }
        {/*<IconButton title="Options"><FaCog /><ButtonLabel>Options</ButtonLabel></IconButton>*/ }
      </ControlPanel>
      <div
        style={ {
          display: "flex",
          flexDirection: "column",
          maxHeight: 440,
          overflowY: "scroll",
        } }
      >
        { events.map((event) => (
          <React.Fragment key={ event.id }>
            { isImageTypeId(event.type_id) && <LoadImage data={ event } own={ event.user_id === user._id } /> }
            { !isImageTypeId(event.type_id) && <MessageBubble own={ event.user_id === user._id }>
              <div className="message">{ event.message }</div>
              { event.changes && <div className="changes">{ event.changes }</div> }
              { event.created_at && (
                <div className="time">
                  { event.user_display_name
                    ? `${event.user_display_name} - `
                    : "" }
                  { DateTime.fromISO(event.created_at, { zone: 'utc' }).toLocal().toFormat("MMM d, h:mm a") }
                </div>
              ) }
            </MessageBubble> }
          </React.Fragment>
        )) }
      </div>
    </EventContainer>
  );
};

export default ProspectEvent;
